import { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { PortalLogin } from "./portal/PortalLogin";
import { PortalRestricted } from "./portal/PortalRestricted";
import { PortalPhotos } from "./portal/PortalPhotos";
import { PortalProgress } from "./portal/PortalProgress";
import { PortalMessages } from "./portal/PortalMessages";
import { PortalSidebar } from "./portal/PortalSidebar";
import { PortalQuoteView } from "./portal/PortalQuoteView";

export default function Portal() {
  const [booking, setBooking] = useState(null);
  const [quote, setQuote] = useState(null);
  const [checking, setChecking] = useState(true);
  const [uploads, setUploads] = useState([]);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [uploadingCount, setUploadingCount] = useState(0);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [payInfo, setPayInfo] = useState({ items: [], paid_to_date: 0, balance: 0, total_inc_vat: 0 });
  const paymentHistory = useMemo(() => (payInfo.items || []).filter(p => p.payment_status !== "initiated"), [payInfo.items]);
  const [payForm, setPayForm] = useState({ amount: "", method: "bacs", payment_ref: "", paid_at: "", note: "" });
  const [documents, setDocuments] = useState([]);
  const [progress, setProgress] = useState([]);

  const fetchData = useCallback(async () => {
    const b = await api.get("/portal/booking");
    setBooking(b.data);
    const [u, m, p, docs, prog] = await Promise.all([
      api.get("/portal/uploads"),
      api.get("/portal/messages"),
      api.get("/portal/payments"),
      api.get("/portal/documents"),
      api.get("/portal/photos"),
    ]);
    setUploads(u.data.items);
    setMessages(m.data.items);
    setPayInfo(p.data);
    setDocuments(docs.data.items);
    setProgress(prog.data.items || []);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const me = await api.get("/portal/me");
        if (me.data.kind === "quote") { setQuote(me.data.quote); }
        else { await fetchData(); }
      }
      catch { setBooking(null); setQuote(null); }
      finally { setChecking(false); }
    })();
  }, [fetchData]);

  const login = async (reference, email) => {
    try {
      const r = await api.post("/portal/login", { reference, email });
      if (r.data.kind === "quote") {
        setQuote(r.data.quote); setBooking(null);
        toast.success("Welcome — here's your saved quote");
      } else {
        setBooking(r.data.booking); setQuote(null);
        toast.success("Welcome back");
        fetchData().catch(() => {});
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    }
  };

  const logout = async () => {
    try { await api.post("/portal/logout"); } catch (e) { console.debug("Logout request failed (session cleared locally):", e); }
    setBooking(null); setQuote(null);
  };

  const upload = async (slot, file) => {
    const fd = new FormData();
    fd.append("slot", slot); fd.append("file", file);
    try {
      await api.post("/portal/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded");
      fetchData().catch(() => {});
    } catch { toast.error("Upload failed"); }
  };

  const del = async (id) => {
    await api.delete(`/portal/upload/${id}`);
    fetchData().catch(() => {});
  };

  const addAttachments = async (files) => {
    for (const f of files) {
      setUploadingCount(c => c + 1);
      setUploadProgress(0);
      try {
        const fd = new FormData(); fd.append("file", f);
        const r = await api.post("/portal/message/attachment", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (e) => setUploadProgress(Math.round((e.loaded / (e.total || 1)) * 100)),
        });
        const isImage = (r.data.content_type || "").startsWith("image/");
        setAttachments(p => [...p, { id: r.data.id, filename: r.data.filename, content_type: r.data.content_type, isImage, preview: isImage ? URL.createObjectURL(f) : null }]);
      } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
      setUploadingCount(c => Math.max(0, c - 1));
    }
  };

  const send = async () => {
    if (!newMsg.trim() && attachments.length === 0) return;
    await api.post("/portal/message", { body: newMsg, attachments: attachments.map(a => a.id) });
    setNewMsg(""); setAttachments([]); setUploadProgress(0);
    fetchData().catch(() => {});
  };

  const reportPayment = async () => {
    if (!payForm.amount || parseFloat(payForm.amount) <= 0) { toast.error("Enter the amount you paid"); return; }
    try {
      await api.post("/portal/payment", { ...payForm, amount: parseFloat(payForm.amount) });
      toast.success("Payment submitted — we'll confirm it once reconciled");
      setPayForm({ amount: "", method: "bacs", payment_ref: "", paid_at: "", note: "" });
      fetchData().catch(() => {});
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not submit"); }
  };

  const isPaid = booking ? !["pending_payment", "site_visit_booked"].includes(booking.status) : false;
  const payDeposit = async () => {
    try {
      const co = await api.post("/payments/checkout", { reference: booking.reference, origin: window.location.origin });
      window.location.href = co.data.url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start checkout"); }
  };

  if (checking) {
    return (
      <main data-testid="portal-loading" className="px-6 md:px-12 lg:px-16 pt-24 pb-24 flex justify-center">
        <Loader2 className="animate-spin text-[#C79A3B]" size={28} />
      </main>
    );
  }

  if (quote) return <PortalQuoteView quote={quote} onLogout={logout} />;
  if (!booking) return <PortalLogin onLogin={login} />;

  return (
    <main data-testid="portal-page" className="px-4 sm:px-6 md:px-12 lg:px-16 pt-8 sm:pt-12 pb-24">
      <div className="flex justify-between items-start gap-3 mb-8">
        <div className="min-w-0">
          <div className="label-eyebrow">Client Portal</div>
          <h1 className="font-black tracking-tighter text-3xl sm:text-4xl md:text-5xl break-all">{booking.reference}</h1>
        </div>
        <button data-testid="portal-logout" onClick={logout} className="de-btn-secondary shrink-0 !px-4 !py-2.5 sm:!px-8 sm:!py-[0.95rem]">Sign out</button>
      </div>

      {!isPaid && <PortalRestricted booking={booking} payDeposit={payDeposit} />}

      {isPaid && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            <PortalProgress photos={progress} />
            <PortalPhotos uploads={uploads} onUpload={upload} onDelete={del} />
            <PortalMessages
              messages={messages} attachments={attachments} setAttachments={setAttachments}
              newMsg={newMsg} setNewMsg={setNewMsg} uploadingCount={uploadingCount}
              uploadProgress={uploadProgress} addAttachments={addAttachments} send={send} />
          </div>
          <PortalSidebar
            booking={booking} payInfo={payInfo} paymentHistory={paymentHistory}
            payForm={payForm} setPayForm={setPayForm} reportPayment={reportPayment} documents={documents} />
        </div>
      )}
    </main>
  );
}
